import { guestApi } from '../../api/guest';
import type { SedrComment, SedrFile, SedrPhase, SedrRequirement } from '../../api/sedr';
import { sedrApi } from '../../api/sedr';
import { buildSedrRoomUrl } from '../../app/navigationState';
import { fileTypeIconFor, resolveFilePreview } from '../../files/fileTypes';
import { applyI18n, t } from '../../i18n';
import { fileTypeImg } from '../../ui/fileTypeImg';
import { iconImg } from '../../ui/icons';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { showLinkCopyDialog } from '../../ui/linkCopyDialog';
import { formatFileSize } from '../../crypto/sharedFolderFile';
import { showSedrFilePickerDialog, type SedrFilePickerUploadResult } from './sedrFilePickerDialog';
import { sedrFileParentPath } from './sedrEvidenceExplorer';
import {
  formatSedrCommentFileToken,
  insertSedrCommentFileToken,
  renderSedrCommentBodyHtml,
} from './sedrCommentBody';
import { formatSedrDate, formatSedrCommentStatusFlag, isSedrApprovalDividerComment, isActiveForSupersedeTarget, isSedrLifecycleClosed, canMutateRequirementLinks, statusBadgeClass, displaySedrCategory, tSedrLinkTypeName, tSedrStatus } from './sedrUi';
import { escapeAttr, escapeHtml } from './sedrEscape';
import { showSedrEditRequirementDialog } from './sedrEditRequirementDialog';
import { showSedrSupersedeTargetDialog } from './sedrSupersedeTargetDialog';
import { showSedrAddRequirementLinkDialog } from './sedrAddRequirementLinkDialog';
import { formatApprovalValidityLabel, formatApprovalGraceLabel } from './sedrApprovalValidity';
import { allowedStatusActions } from './sedrRequirementTransitions';
import { showInputDialog } from '../../ui/inputDialog';
import { sedrErrorMessageKey } from './sedrErrors';
import type { BlobUploadProgress } from '../../api/blobUpload';

export interface SedrRequirementPanelOptions {
  roomId: string;
  requirement: SedrRequirement | null;
  files: SedrFile[];
  phases?: SedrPhase[];
  /** Used for supersede target picker; falls back to listRequirements. */
  requirements?: SedrRequirement[];
  canWrite: boolean;
  onUpdated: () => Promise<void> | void;
  onClose?: () => void;
  onOpenFile?: (file: SedrFile, context?: { gallery: SedrFile[] }) => void | Promise<void>;
  onDownloadFile?: (file: SedrFile) => void | Promise<void>;
  onOpenRequirement?: (requirementId: string) => void;
  onUploadFiles?: (
    parentId: string | null,
    files: File[],
    onProgress: (progress: BlobUploadProgress) => void,
  ) => Promise<SedrFilePickerUploadResult>;
  guest?: {
    roomToken?: string;
    guestInviteId?: string;
    accountRoomId?: string;
    canApprove: boolean;
    canComment?: boolean;
    onApprove?: () => Promise<void>;
    onDeny?: (reason: string) => Promise<void>;
  };
}

/**
 * Renders a read-first requirement detail panel.
 * Editing opens a modal; status changes use explicit transition actions.
 */
export async function renderSedrRequirementPanel(
  host: HTMLElement,
  options: SedrRequirementPanelOptions,
): Promise<void> {
  const { roomId, canWrite, guest } = options;
  let req = options.requirement;
  let showArchivedArtifacts = false;
  let files = options.files;

  const filesById = () => {
    const map = new Map<string, SedrFile>();
    for (const f of files) map.set(f.id, f);
    return map;
  };

  const pickerUpload = options.onUploadFiles
    ? async (
        parentId: string | null,
        selected: File[],
        onProgress: (progress: BlobUploadProgress) => void,
      ): Promise<SedrFilePickerUploadResult> => {
        const result = await options.onUploadFiles!(parentId, selected, onProgress);
        files = result.files;
        return result;
      }
    : undefined;

  const loadComments = async (): Promise<SedrComment[]> => {
    if (!req) return [];
    if (guest?.accountRoomId) {
      return guestApi.listComments(guest.accountRoomId, req.id);
    }
    if (guest?.roomToken && guest?.guestInviteId) {
      return sedrApi.guestListComments(guest.roomToken, guest.guestInviteId, req.id);
    }
    return sedrApi.listComments(roomId, req.id);
  };

  const refreshRequirement = async () => {
    if (!req) return;
    if (guest?.accountRoomId) {
      const list = await guestApi.listRequirements(guest.accountRoomId);
      req = list.find((r) => r.id === req!.id) ?? req;
    } else if (guest?.roomToken && guest?.guestInviteId) {
      const list = await sedrApi.guestListRequirements(guest.roomToken, guest.guestInviteId);
      req = list.find((r) => r.id === req!.id) ?? req;
    } else {
      const list = await sedrApi.listRequirements(roomId);
      req = list.find((r) => r.id === req!.id) ?? req;
    }
    await options.onUpdated();
  };

  const openLinkedFile = (fileId: string | null | undefined, gallery?: SedrFile[]) => {
    if (!fileId || !options.onOpenFile) return;
    const file = filesById().get(fileId);
    if (!file?.blobId) return;
    if (gallery && gallery.length > 1) {
      void options.onOpenFile(file, { gallery });
      return;
    }
    void options.onOpenFile(file);
  };

  const renderFileLink = (fileId: string | null, fallbackName: string | null): string => {
    if (!fileId && !fallbackName) return '—';
    const file = fileId ? filesById().get(fileId) : undefined;
    const name = file?.name ?? fallbackName ?? '—';
    const canOpen = Boolean(file?.blobId && options.onOpenFile);
    if (!canOpen) return escapeHtml(name);
    return `<button type="button" class="button subtle sedr-file-link" data-open-file-id="${escapeAttr(fileId!)}">${escapeHtml(name)}</button>`;
  };

  const bindOpenFileButtons = (scope: ParentNode) => {
    for (const btn of scope.querySelectorAll<HTMLButtonElement>('[data-open-file-id]')) {
      const fileId = btn.getAttribute('data-open-file-id');
      if (!fileId) continue;
      const file = filesById().get(fileId);
      if (file?.blobId) {
        const icon = fileTypeImg(fileTypeIconFor(file.name, file.contentType), 16);
        btn.prepend(icon);
      }
      btn.addEventListener('click', () => openLinkedFile(fileId));
    }
  };

  const statusHelpKey = (status: string): string | null => {
    if (status === 'PendingUpload') return 'sedr.status.PendingUpload.help';
    if (status === 'Denied') return 'sedr.status.Denied.help';
    if (status === 'UnderReview') return 'sedr.status.UnderReview.help';
    if (status === 'NotApplicable') return 'sedr.status.NotApplicable.help';
    if (status === 'Superseded') return 'sedr.status.Superseded.help';
    if (status === 'Retired') return 'sedr.status.Retired.help';
    if (status === 'Archived') return 'sedr.status.Archived.help';
    return null;
  };

  const lifecycleApproveLabelKey = (requested: string | null | undefined): string => {
    switch (requested) {
      case 'NotApplicable':
        return 'sedr.drawer.approveNotApplicable';
      case 'Superseded':
        return 'sedr.drawer.approveSuperseded';
      case 'Retired':
        return 'sedr.drawer.approveRetired';
      case 'Archived':
        return 'sedr.drawer.approveArchived';
      default:
        return guest ? 'sedr.guest.signOff' : 'sedr.drawer.approve';
    }
  };

  const renderStatusActions = (current: SedrRequirement, candidates: SedrRequirement[]): string => {
    const hasLifecycleRequest = Boolean(current.requestedLifecycleStatus);
    const canApproveLifecycle = Boolean(
      (guest?.canApprove && current.canClientApprove)
      || (!guest && current.canApprove),
    ) && hasLifecycleRequest;
    const canApproveDischarge = Boolean(
      (guest?.canApprove && current.canClientApprove)
      || (!guest && current.canApprove),
    ) && (
      (current.status === 'UnderReview' && !hasLifecycleRequest)
      || (current.status === 'ApprovedDischarged' && current.renewalStatus === 'UnderReview')
    );
    const parts: string[] = [];
    const isApproved = current.status === 'ApprovedDischarged';
    const renewalActions = isApproved
      ? allowedStatusActions(
          current.status,
          canApproveDischarge,
          current.renewalStatus,
        )
      : [];

    if (canWrite && !isSedrLifecycleClosed(current.status) && !hasLifecycleRequest && !isApproved) {
      if (current.status === 'PendingUpload' || current.status === 'Denied') {
        parts.push(`<button type="button" class="button subtle" id="sedr-req-mark-ready" data-i18n="sedr.drawer.markAsReady"></button>`);
      }
      if (current.status === 'UnderReview') {
        parts.push(`<button type="button" class="button subtle" id="sedr-req-withdraw" data-i18n="sedr.drawer.withdraw"></button>`);
      }
      parts.push(`<button type="button" class="button subtle" id="sedr-req-lifecycle-na" data-i18n="sedr.drawer.requestNotApplicable"></button>`);
      if (candidates.some((r) => r.id !== current.id && isActiveForSupersedeTarget(r.status))) {
        parts.push(`<button type="button" class="button subtle" id="sedr-req-lifecycle-supersede" data-i18n="sedr.drawer.requestSuperseded"></button>`);
      }
      parts.push(`<button type="button" class="button subtle" id="sedr-req-lifecycle-retire" data-i18n="sedr.drawer.requestRetired"></button>`);
      parts.push(`<button type="button" class="button subtle" id="sedr-req-lifecycle-archive" data-i18n="sedr.drawer.requestArchived"></button>`);
    }

    if (canWrite && isApproved && renewalActions.includes('markReady')) {
      parts.push(`<button type="button" class="button subtle" id="sedr-req-mark-ready" data-i18n="sedr.drawer.markAsReady"></button>`);
    }
    if (canWrite && isApproved && renewalActions.includes('withdraw')) {
      parts.push(`<button type="button" class="button subtle" id="sedr-req-withdraw" data-i18n="sedr.drawer.withdraw"></button>`);
    }

    if (canWrite && hasLifecycleRequest && !isApproved) {
      parts.push(`<button type="button" class="button subtle" id="sedr-req-lifecycle-withdraw" data-i18n="sedr.drawer.withdrawLifecycleRequest"></button>`);
    }

    if (canApproveLifecycle) {
      parts.push(`<button type="button" class="button subtle" id="sedr-req-deny" data-i18n="sedr.drawer.denyLifecycleRequest"></button>`);
      parts.push(
        `<button type="button" class="button primary" id="sedr-req-approve" data-i18n="${lifecycleApproveLabelKey(current.requestedLifecycleStatus)}"></button>`,
      );
    } else if (canApproveDischarge) {
      parts.push(`<button type="button" class="button subtle" id="sedr-req-deny" data-i18n="sedr.drawer.deny"></button>`);
      parts.push(
        `<button type="button" class="button primary" id="sedr-req-approve" data-i18n="${guest ? 'sedr.guest.signOff' : 'sedr.drawer.approve'}"></button>`,
      );
    }

    if (parts.length === 0) return '';
    return `<div class="sedr-requirement-panel__toolbar" id="sedr-req-toolbar">${parts.join('')}</div>`;
  };

  const renderRenewalChip = (current: SedrRequirement): string => {
    if (current.status !== 'ApprovedDischarged' || !current.renewalStatus) return '';
    const key = current.renewalStatus === 'UnderReview'
      ? 'sedr.requirement.renewalUnderReview'
      : 'sedr.requirement.renewalAwaitingMarkReady';
    return `<span class="sedr-requirement-panel__renewal-chip">${escapeHtml(t(key))}</span>`;
  };

  const renderLifecycleBanner = (current: SedrRequirement): string => {
    if (!current.requestedLifecycleStatus) return '';
    const target = current.supersededByRequirementCode
      ? ` → ${escapeHtml(current.supersededByRequirementCode)}`
      : '';
    return `<p class="sedr-requirement-panel__lifecycle-banner">
      ${escapeHtml(t('sedr.drawer.lifecycleRequestPending', {
        status: tSedrStatus(current.requestedLifecycleStatus),
      }))}${target}
    </p>`;
  };

  const render = async () => {
    if (!req) {
      host.className = 'sedr-requirement-panel-host sedr-requirement-panel sedr-requirement-panel--empty';
      host.innerHTML = `
        <p class="muted sedr-requirement-panel__empty" data-i18n="sedr.drawer.selectRequirement"></p>
      `;
      applyI18n(host);
      return;
    }

    const current = req;
    const allRequirements = options.requirements
      ?? await sedrApi.listRequirements(roomId).catch(() => [] as SedrRequirement[]);
    const showCommentForm = canWrite && (!guest || Boolean(guest.canComment));
    const helpKey = statusHelpKey(current.status);
    const validityLabel = formatApprovalValidityLabel(
      current.approvalValidityAmount,
      current.approvalValidityUnit,
      current.approvalValidityDays,
      t,
    );
    const graceLabel = formatApprovalGraceLabel(current.approvalGraceDays, t);
    const canEditMetadata = canWrite
      && current.status !== 'ApprovedDischarged'
      && !isSedrLifecycleClosed(current.status);
    const canLinkEvidence = canWrite
      && (
        (current.status !== 'ApprovedDischarged' && !isSedrLifecycleClosed(current.status))
        || Boolean(current.inApprovalGrace)
      );
    const canArchiveEvidence = canEditMetadata;

    host.className = 'sedr-requirement-panel-host sedr-requirement-panel';
    host.innerHTML = `
      <header class="sedr-requirement-panel__header">
        ${options.onClose
          ? `<button type="button" class="button ghost sedr-requirement-panel__close" id="sedr-req-close" data-i18n-aria-label="common.back"></button>`
          : ''}
        <div class="sedr-requirement-panel__title-block">
          <h2><code>${escapeHtml(current.requirementCode)}</code> · ${escapeHtml(current.title)}</h2>
          <span class="${statusBadgeClass(current.status)}"${helpKey ? ` title="${escapeAttr(t(helpKey))}"` : ''}>${tSedrStatus(current.status)}</span>
          ${renderRenewalChip(current)}
        </div>
        ${!guest || guest.accountRoomId
          ? `<button type="button" class="button subtle sedr-requirement-panel__link" id="sedr-req-copy-link" data-i18n-aria-label="sedr.requirement.copyLink"></button>`
          : ''}
      </header>

      ${helpKey ? `<p class="muted sedr-requirement-panel__status-help">${escapeHtml(t(helpKey))}</p>` : ''}
      ${renderLifecycleBanner(current)}

      ${renderStatusActions(current, allRequirements)}

      <section class="sedr-requirement-panel__section" id="sedr-req-details-section">
        <div class="sedr-requirement-panel__section-header">
          <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.drawer.details"></h3>
          ${canEditMetadata ? `<button type="button" class="button ghost sedr-requirement-panel__section-action" id="sedr-req-edit" data-i18n="sedr.drawer.editShort"></button>` : ''}
        </div>
        <dl class="sedr-requirement-panel__meta">
          <div class="sedr-requirement-panel__span-2"><dt data-i18n="sedr.table.description"></dt><dd class="sedr-requirement-panel__description">${current.description ? escapeHtml(current.description) : '—'}</dd></div>
          <div><dt data-i18n="sedr.table.category"></dt><dd>${escapeHtml(displaySedrCategory(current.category, current.categoryName))}</dd></div>
          <div><dt data-i18n="sedr.table.owner"></dt><dd>${escapeHtml(current.ownerName ?? '—')}</dd></div>
          <div><dt data-i18n="sedr.phases.field"></dt><dd>${escapeHtml(current.phaseName ?? t('sedr.phases.unassigned'))}</dd></div>
          ${current.approvedAt ? `<div><dt data-i18n="sedr.table.approvedBy"></dt><dd>${escapeHtml(current.approvedByDisplay ?? '—')}${current.approvedAt ? ` · ${formatSedrDate(current.approvedAt)}` : ''}</dd></div>` : ''}
          <div><dt data-i18n="sedr.requirement.approvalValidity"></dt><dd>${escapeHtml(validityLabel)}${current.approvalExpiresAt ? ` · ${formatSedrDate(current.approvalExpiresAt)}` : ''}${graceLabel ? ` · ${escapeHtml(graceLabel)}` : ''}</dd></div>
        </dl>
      </section>

      <section class="sedr-requirement-panel__section" id="sedr-req-links-section">
        <div class="sedr-requirement-panel__section-header">
          <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.links.title"></h3>
          <div class="sedr-requirement-panel__section-actions">
            ${canWrite && canMutateRequirementLinks(current.status)
              ? `<button type="button" class="button ghost sedr-requirement-panel__section-action" id="sedr-req-add-link" data-i18n="sedr.links.add"></button>`
              : ''}
          </div>
        </div>
        <ul class="sedr-req-links" id="sedr-req-links"></ul>
      </section>

      <section class="sedr-requirement-panel__section">
        <div class="sedr-requirement-panel__section-header">
          <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.table.artifacts"></h3>
          ${canLinkEvidence ? `<button type="button" class="button ghost sedr-requirement-panel__section-action" id="sedr-req-link-artifact" data-i18n="sedr.drawer.linkArtifact"></button>` : ''}
        </div>
        <ul class="sedr-artifact-list" id="sedr-req-artifacts"></ul>
        <button type="button" class="button ghost sedr-artifact-archive-toggle" id="sedr-req-show-archived" hidden></button>
      </section>

      <section class="sedr-requirement-panel__section">
        <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.drawer.qa"></h3>
        <ul class="sedr-comments" id="sedr-req-comments"></ul>
        ${showCommentForm ? `
        <form class="form-grid sedr-requirement-panel__comment-form" id="sedr-req-comment-form">
          <label>
            <span data-i18n="sedr.drawer.commentPlaceholder"></span>
            <textarea name="body" required maxlength="2000" rows="3" data-i18n-placeholder="sedr.drawer.commentPlaceholder"></textarea>
          </label>
          <div class="sedr-requirement-panel__actions">
            <button type="button" class="button ghost" id="sedr-req-comment-link-file" data-i18n="sedr.drawer.linkFile"></button>
            <button type="submit" class="button subtle" data-i18n="sedr.drawer.addComment"></button>
          </div>
        </form>` : guest ? `<p class="muted" data-i18n="sedr.guest.commentingDisabled"></p>` : ''}
      </section>
    `;
    applyI18n(host);
    bindOpenFileButtons(host);

    const closeBtn = host.querySelector('#sedr-req-close') as HTMLButtonElement | null;
    if (closeBtn && options.onClose) {
      closeBtn.append(iconImg('chevron-left', 18));
      closeBtn.addEventListener('click', () => options.onClose?.());
    }

    const linkBtn = host.querySelector('#sedr-req-copy-link');
    linkBtn?.append(iconImg('link', 18));
    linkBtn?.addEventListener('click', () => {
      const url = `${window.location.origin}${buildSedrRoomUrl(roomId, 'compliance', { requirementId: current.id })}`;
      void showLinkCopyDialog(async () => url, {
        titleKey: 'sedr.requirement.copyLinkTitle',
        warningKey: 'sedr.requirement.copyLinkWarning',
        createdKey: 'sedr.requirement.copyLinkCreated',
        creatingTitleKey: 'sedr.requirement.copyLinkCreatingTitle',
        creatingDetailKey: 'sedr.requirement.copyLinkCreatingDetail',
      }, {
        helpTopic: dialogHelpTopic('sedrRequirementLink'),
        requireCopyBeforeClose: false,
        copyButtonKey: 'info.owner.copyLink',
        closeButtonKey: 'common.close',
      });
    });

    const linksEl = host.querySelector('#sedr-req-links') as HTMLUListElement;
    const links = current.links ?? [];
    if (links.length === 0) {
      linksEl.innerHTML = `<li class="muted" data-i18n="sedr.links.empty"></li>`;
      applyI18n(linksEl);
    } else {
      linksEl.replaceChildren();
      for (const link of links) {
        const li = document.createElement('li');
        li.className = 'sedr-req-link';
        const canRemove = canWrite
          && canMutateRequirementLinks(current.status)
          && canMutateRequirementLinks(link.otherStatus);

        const typeLabel = document.createElement('span');
        typeLabel.className = 'sedr-req-link__type muted';
        typeLabel.textContent = tSedrLinkTypeName(
          link.linkType,
          link.linkTypeName,
          link.direction === 'incoming',
        );

        const openBtn = document.createElement('button');
        openBtn.type = 'button';
        openBtn.className = 'text-link sedr-req-link__target';
        openBtn.textContent = `${link.otherRequirementCode} · ${link.otherTitle}`;
        openBtn.addEventListener('click', () => {
          options.onOpenRequirement?.(link.otherRequirementId);
        });

        const badge = document.createElement('span');
        badge.className = statusBadgeClass(link.otherStatus);
        badge.textContent = tSedrStatus(link.otherStatus);

        const main = document.createElement('div');
        main.className = 'sedr-req-link__main';
        main.append(typeLabel, openBtn, badge);
        li.append(main);

        if (canRemove) {
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'button ghost sedr-req-link__remove';
          removeBtn.setAttribute('aria-label', t('sedr.links.remove'));
          removeBtn.textContent = '×';
          removeBtn.addEventListener('click', () => {
            void (async () => {
              try {
                await sedrApi.deleteRequirementLink(roomId, current.id, link.id);
                await refreshRequirement();
                await render();
              } catch (err) {
                window.alert(t(sedrErrorMessageKey(err)));
              }
            })();
          });
          li.append(removeBtn);
        }

        linksEl.append(li);
      }
    }

    host.querySelector('#sedr-req-add-link')?.addEventListener('click', () => {
      void (async () => {
        try {
          const linkTypes = await sedrApi.listRequirementLinkTypes(roomId);
          const result = await showSedrAddRequirementLinkDialog({
            source: current,
            candidates: allRequirements,
            linkTypes,
          });
          if (!result) return;
          await sedrApi.createRequirementLink(roomId, current.id, result);
          await refreshRequirement();
          await render();
        } catch (err) {
          window.alert(t(sedrErrorMessageKey(err)));
        }
      })();
    });

    const activeArtifacts = current.artifacts.filter((a) => !a.archivedAt);
    const archivedArtifacts = current.artifacts.filter((a) => a.archivedAt);
    const imageGalleryFiles = (): SedrFile[] => {
      const map = filesById();
      const gallery: SedrFile[] = [];
      for (const artifact of activeArtifacts) {
        const file = map.get(artifact.fileId);
        if (!file?.blobId) continue;
        if (resolveFilePreview(file.name, file.contentType ?? artifact.contentType).kind !== 'image') continue;
        gallery.push(file);
      }
      return gallery;
    };

    const renderArtifactItem = (a: SedrRequirement['artifacts'][number], archived: boolean) => {
      const li = document.createElement('li');
      li.className = `sedr-artifact-item${archived ? ' sedr-artifact-item--archived' : ''}`;
      const file = filesById().get(a.fileId);
      const pathPrefix = file ? sedrFileParentPath(files, file) : '/';
      const contentType = file?.contentType ?? a.contentType;
      const canOpen = Boolean(file?.blobId && options.onOpenFile);
      const canDownload = Boolean(file?.blobId && options.onDownloadFile);
      const fullPath = `${pathPrefix}${a.name}`;

      const row = document.createElement('div');
      row.className = 'sedr-artifact-row';

      const label = document.createElement('div');
      label.className = 'sedr-artifact-row__label';
      label.title = fullPath;
      const pathText = document.createElement('span');
      pathText.className = 'sedr-artifact-row__path';
      pathText.textContent = fullPath;
      label.append(fileTypeImg(fileTypeIconFor(a.name, contentType), 16), pathText);

      const openArtifact = () => {
        if (archived) {
          openLinkedFile(a.fileId);
          return;
        }
        const gallery = imageGalleryFiles();
        const isImage = file
          ? resolveFilePreview(file.name, file.contentType ?? a.contentType).kind === 'image'
          : false;
        openLinkedFile(a.fileId, isImage ? gallery : undefined);
      };

      const downloadArtifact = () => {
        const target = filesById().get(a.fileId);
        if (target?.blobId && options.onDownloadFile) {
          void options.onDownloadFile(target);
        }
      };

      if (canOpen) {
        row.classList.add('sedr-artifact-row--actionable');
        label.classList.add('sedr-artifact-row__label--clickable');
        label.addEventListener('click', openArtifact);
      }

      const actions = document.createElement('div');
      actions.className = 'sedr-artifact-row__actions';

      if (canDownload) {
        const downloadBtn = document.createElement('button');
        downloadBtn.type = 'button';
        downloadBtn.className = 'button ghost sedr-artifact-row__btn';
        downloadBtn.dataset.artifactAction = 'download';
        downloadBtn.textContent = t('files.owner.download');
        downloadBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          downloadArtifact();
        });
        actions.append(downloadBtn);
      }

      if (canArchiveEvidence && !guest) {
        const archiveBtn = document.createElement('button');
        archiveBtn.type = 'button';
        archiveBtn.className = 'button ghost sedr-artifact-row__btn';
        archiveBtn.dataset.artifactAction = archived ? 'restore' : 'archive';
        archiveBtn.textContent = archived ? t('sedr.artifact.restore') : t('sedr.artifact.archive');
        archiveBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          void (async () => {
            if (archived) {
              await sedrApi.unarchiveArtifact(roomId, current.id, a.id);
            } else {
              await sedrApi.archiveArtifact(roomId, current.id, a.id);
            }
            await refreshRequirement();
            await render();
          })();
        });
        actions.append(archiveBtn);
      }

      row.append(label, actions);
      li.append(row);
      return li;
    };

    const artifactsEl = host.querySelector('#sedr-req-artifacts') as HTMLUListElement;
    if (activeArtifacts.length === 0 && archivedArtifacts.length === 0) {
      artifactsEl.innerHTML = `<li class="muted">—</li>`;
    } else {
      artifactsEl.replaceChildren();
      for (const a of activeArtifacts) {
        artifactsEl.append(renderArtifactItem(a, false));
      }
      if (showArchivedArtifacts) {
        for (const a of archivedArtifacts) {
          artifactsEl.append(renderArtifactItem(a, true));
        }
      }
    }

    const archivedToggle = host.querySelector('#sedr-req-show-archived') as HTMLButtonElement | null;
    if (archivedToggle && archivedArtifacts.length > 0) {
      archivedToggle.hidden = false;
      archivedToggle.textContent = showArchivedArtifacts
        ? t('sedr.artifact.hideArchived', { count: archivedArtifacts.length })
        : t('sedr.artifact.showArchived', { count: archivedArtifacts.length });
      archivedToggle.onclick = () => {
        showArchivedArtifacts = !showArchivedArtifacts;
        void render();
      };
    }

    host.querySelector('#sedr-req-edit')?.addEventListener('click', () => {
      showSedrEditRequirementDialog({
        roomId,
        requirement: current,
        phases: options.phases ?? [],
        onUpdated: async () => {
          await refreshRequirement();
          await render();
        },
      });
    });

    host.querySelector('#sedr-req-link-artifact')?.addEventListener('click', () => {
      void (async () => {
        const excludeFileIds = current.artifacts.map((a) => a.fileId);
        const fileId = await showSedrFilePickerDialog({
          files,
          excludeFileIds,
          formatSize: formatFileSize,
          titleKey: 'sedr.picker.titleArtifact',
          hintKey: 'sedr.picker.hintArtifact',
          onUploadFiles: pickerUpload,
        });
        if (!fileId) return;
        await sedrApi.linkArtifact(roomId, current.id, fileId);
        await refreshRequirement();
        await render();
      })();
    });

    host.querySelector('#sedr-req-mark-ready')?.addEventListener('click', () => {
      void (async () => {
        req = await sedrApi.patchRequirement(roomId, current.id, { status: 'UnderReview' });
        await refreshRequirement();
        await render();
      })();
    });

    host.querySelector('#sedr-req-withdraw')?.addEventListener('click', () => {
      void (async () => {
        req = await sedrApi.patchRequirement(roomId, current.id, { status: 'PendingUpload' });
        await refreshRequirement();
        await render();
      })();
    });

    const requestLifecycle = async (
      status: 'NotApplicable' | 'Superseded' | 'Retired' | 'Archived',
      supersededByRequirementId?: string,
    ) => {
      req = await sedrApi.patchRequirement(roomId, current.id, {
        requestedLifecycleStatus: status,
        supersededByRequirementId: supersededByRequirementId ?? null,
      });
      await options.onUpdated();
      await refreshRequirement();
      await render();
    };

    host.querySelector('#sedr-req-lifecycle-na')?.addEventListener('click', () => {
      void requestLifecycle('NotApplicable');
    });
    host.querySelector('#sedr-req-lifecycle-retire')?.addEventListener('click', () => {
      void requestLifecycle('Retired');
    });
    host.querySelector('#sedr-req-lifecycle-archive')?.addEventListener('click', () => {
      void requestLifecycle('Archived');
    });
    host.querySelector('#sedr-req-lifecycle-supersede')?.addEventListener('click', () => {
      void (async () => {
        const candidates = allRequirements.filter(
          (r) => r.id !== current.id && isActiveForSupersedeTarget(r.status),
        );
        if (candidates.length === 0) return;
        const targetId = await showSedrSupersedeTargetDialog({ candidates });
        if (!targetId) return;
        await requestLifecycle('Superseded', targetId);
      })();
    });
    host.querySelector('#sedr-req-lifecycle-withdraw')?.addEventListener('click', () => {
      void (async () => {
        req = await sedrApi.patchRequirement(roomId, current.id, {
          clearRequestedLifecycleStatus: true,
        });
        await options.onUpdated();
        await refreshRequirement();
        await render();
      })();
    });

    host.querySelector('#sedr-req-deny')?.addEventListener('click', () => {
      void (async () => {
        const lifecycle = Boolean(current.requestedLifecycleStatus);
        const reason = await showInputDialog({
          titleKey: lifecycle ? 'sedr.drawer.denyLifecycleRequest' : 'sedr.drawer.deny',
          labelKey: lifecycle
            ? 'sedr.drawer.denyLifecycleReasonPrompt'
            : 'sedr.drawer.denyReasonPrompt',
          confirmLabelKey: 'sedr.drawer.deny',
          multiline: true,
          required: true,
          maxLength: 2000,
        });
        if (!reason) return;
        if (guest?.onDeny) {
          await guest.onDeny(reason);
        } else if (guest?.accountRoomId) {
          await guestApi.denyRequirement(guest.accountRoomId, current.id, reason);
        } else if (guest?.roomToken && guest?.guestInviteId) {
          await sedrApi.guestDenyRequirement(guest.roomToken, guest.guestInviteId, current.id, reason);
        } else {
          req = await sedrApi.patchRequirement(roomId, current.id, {
            status: 'Denied',
            statusChangeReason: reason,
          });
        }
        await options.onUpdated();
        await refreshRequirement();
        await render();
      })();
    });

    host.querySelector('#sedr-req-approve')?.addEventListener('click', () => {
      void (async () => {
        if (guest?.onApprove) {
          await guest.onApprove();
        } else if (guest?.accountRoomId) {
          await guestApi.approveRequirement(guest.accountRoomId, current.id);
        } else if (guest?.roomToken && guest?.guestInviteId) {
          await sedrApi.guestApproveRequirement(guest.roomToken, guest.guestInviteId, current.id);
        } else {
          req = await sedrApi.patchRequirement(roomId, current.id, { status: 'ApprovedDischarged' });
        }
        await options.onUpdated();
        await refreshRequirement();
        await render();
      })();
    });

    host.querySelector('#sedr-req-comment-link-file')?.addEventListener('click', () => {
      void (async () => {
        const form = host.querySelector('#sedr-req-comment-form') as HTMLFormElement | null;
        const textarea = form?.querySelector<HTMLTextAreaElement>('textarea[name="body"]');
        if (!textarea) return;
        const fileId = await showSedrFilePickerDialog({
          files,
          formatSize: formatFileSize,
          titleKey: 'sedr.picker.titleCommentFile',
          hintKey: 'sedr.picker.hintCommentFile',
          onUploadFiles: pickerUpload,
        });
        if (!fileId) return;
        const file = filesById().get(fileId);
        if (!file) return;
        insertSedrCommentFileToken(textarea, formatSedrCommentFileToken(file.id, file.name));
      })();
    });

    host.querySelector('#sedr-req-comment-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      void (async () => {
        const form = e.target as HTMLFormElement;
        const body = String(new FormData(form).get('body') ?? '').trim();
        if (!body) return;
        if (guest?.accountRoomId) {
          await guestApi.addComment(guest.accountRoomId, current.id, body);
        } else if (guest?.roomToken && guest?.guestInviteId) {
          await sedrApi.guestAddComment(guest.roomToken, guest.guestInviteId, current.id, body);
        } else {
          await sedrApi.addComment(roomId, current.id, body);
        }
        form.reset();
        await render();
      })();
    });

    const commentsEl = host.querySelector('#sedr-req-comments') as HTMLUListElement;
    const comments = await loadComments();
    if (comments.length === 0) {
      commentsEl.innerHTML = `<li class="muted" data-i18n="sedr.drawer.noComments"></li>`;
      applyI18n(commentsEl);
    } else {
      for (const c of comments) {
        const li = document.createElement('li');
        if (isSedrApprovalDividerComment(c)) {
          li.className = 'sedr-comment-divider';
          li.setAttribute('role', 'separator');
          li.innerHTML = `
            <div class="sedr-comment-divider__rule" aria-hidden="true"></div>
            <div class="sedr-comment-divider__label">
              <span class="sedr-comment-divider__badge">${escapeHtml(t('sedr.drawer.approvalDivider'))}</span>
              <span class="sedr-comment-divider__meta">${escapeHtml(c.authorDisplayName ?? '—')} · ${formatSedrDate(c.createdAt)}</span>
            </div>
            <div class="sedr-comment-divider__rule" aria-hidden="true"></div>
          `;
          commentsEl.append(li);
          continue;
        }

        const statusFlagText = formatSedrCommentStatusFlag(c);
        li.className = `sedr-comment${statusFlagText ? ' sedr-comment--status-change' : ''}`;
        const statusMeta = statusFlagText
          ? `<span class="sedr-comment__status-flag">${escapeHtml(statusFlagText)}</span>`
          : '';
        const bodyHtml = renderSedrCommentBodyHtml(c.body, renderFileLink);
        li.innerHTML = `<div class="sedr-comment__meta"><strong>${escapeHtml(c.authorDisplayName ?? '—')}</strong>
          <time>${formatSedrDate(c.createdAt)}</time>${statusMeta}</div>
          <p class="sedr-comment__body">${bodyHtml}</p>`;
        commentsEl.append(li);
      }
      bindOpenFileButtons(commentsEl);
    }
  };

  await render();
}
